import { useState } from 'react'
import type { JSX, MouseEvent } from 'react'

import { AlertDialog, Button } from '@buffer-mono/popcorn'

import type { CollaborationNote } from '~publish/hooks/notes/notesQueries'
import type { DeleteNoteFailure } from '~publish/hooks/notes/useDeleteNote'

import { usePostNotesContext } from './PostNotesProvider'

const ERROR_COPY: Record<DeleteNoteFailure, string> = {
  permission: 'You do not have permission to delete this Note.',
  request: 'The Note could not be deleted. Try again.',
}

export function DeleteNoteDialog({
  note,
  open,
  onOpenChange,
}: {
  note: CollaborationNote
  open: boolean
  onOpenChange: (open: boolean) => void
}): JSX.Element {
  const { remove } = usePostNotesContext()
  const [error, setError] = useState<DeleteNoteFailure | null>(null)

  const changeOpen = (nextOpen: boolean): void => {
    if (remove.pending) return
    if (!nextOpen) setError(null)
    onOpenChange(nextOpen)
  }

  const confirm = async (
    event: MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    event.preventDefault()
    setError(null)
    const result = await remove.submit(note)
    if (result.ok) {
      onOpenChange(false)
    } else {
      setError(result.reason)
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={changeOpen}>
      <AlertDialog.Content>
        <AlertDialog.Title>Delete note?</AlertDialog.Title>
        <AlertDialog.Description>
          This Note will be permanently deleted. This action cannot be undone.
        </AlertDialog.Description>
        {error && <div role="alert">{ERROR_COPY[error]}</div>}
        <AlertDialog.Actions>
          <AlertDialog.Cancel>
            <Button variant="tertiary" disabled={remove.pending}>
              Cancel
            </Button>
          </AlertDialog.Cancel>
          <AlertDialog.Action>
            <Button
              variant="critical"
              loading={remove.pending}
              disabled={!remove.enabled || remove.pending}
              onClick={(event) => void confirm(event)}
            >
              Delete note
            </Button>
          </AlertDialog.Action>
        </AlertDialog.Actions>
      </AlertDialog.Content>
    </AlertDialog>
  )
}
